import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import {
  useSaveUpsellDraft,
  type UpsellDraftContainerPatch,
  type UpsellLinePersistPatch,
} from "@/hooks/use-save-upsell-draft";
import type {
  UpsellBillingMode,
  UpsellLine,
  UpsellWithLines,
} from "@/hooks/use-upsells";
import type { OfferPhase } from "@/lib/offer-calc";
import { templateHourlyRate } from "@/lib/editable-line";

/** Snapshot av lagrede felt for dirty-sammenligning. */
export interface UpsellEditBaseline {
  description: string;
  overhead_pct: number | null;
  discount_pct: number;
  discount_amount: number;
  billing_mode: UpsellBillingMode;
  lines: UpsellLine[];
}

function cloneLines(lines: UpsellLine[]): UpsellLine[] {
  return lines.map((l) => ({ ...l }));
}

export function buildBaseline(upsell: UpsellWithLines): UpsellEditBaseline {
  return {
    description: upsell.description,
    overhead_pct: upsell.overhead_pct,
    discount_pct: upsell.discount_pct ?? 0,
    discount_amount: upsell.discount_amount ?? 0,
    billing_mode: upsell.billing_mode ?? "fixed",
    lines: cloneLines(upsell.lines),
  };
}

function lineFingerprint(l: UpsellLine): string {
  return JSON.stringify({
    id: l.id,
    phase: l.phase,
    description: l.description,
    cost_type: l.cost_type,
    unit: l.unit,
    quantity: l.quantity,
    quantity_hours: l.quantity_hours,
    amount: l.amount,
    estimated_cost: l.estimated_cost,
    template_id: l.template_id,
    rate_basis_hourly: l.rate_basis_hourly,
  });
}

function linesEqual(a: UpsellLine[], b: UpsellLine[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x.id.localeCompare(y.id)).map(lineFingerprint);
  const sb = [...b].sort((x, y) => x.id.localeCompare(y.id)).map(lineFingerprint);
  return sa.every((v, i) => v === sb[i]);
}

function parseOverhead(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const num = Number(t);
  return Number.isFinite(num) ? num : null;
}

function parseDiscount(raw: string): number {
  return Number(raw) || 0;
}

/**
 * Lokal draft for UpsellEditDialog. Ingen DB-skriving før `save()`.
 * Angre = lukk uten lagre (forkast draft) og åpne igjen.
 */
export function useUpsellEditState(
  upsell: UpsellWithLines,
  projectId: string,
  open: boolean,
) {
  const saveMut = useSaveUpsellDraft();
  const baselineRef = useRef<UpsellEditBaseline>(buildBaseline(upsell));
  const [baselineVersion, setBaselineVersion] = useState(0);

  const [description, setDescription] = useState(upsell.description);
  const [overheadInput, setOverheadInput] = useState(
    upsell.overhead_pct != null ? String(upsell.overhead_pct) : "",
  );
  const [discountPctInput, setDiscountPctInput] = useState(
    upsell.discount_pct ? String(upsell.discount_pct) : "",
  );
  const [discountAmountInput, setDiscountAmountInput] = useState(
    upsell.discount_amount ? String(upsell.discount_amount) : "",
  );
  const [billingMode, setBillingMode] = useState<UpsellBillingMode>(
    upsell.billing_mode ?? "fixed",
  );
  const [lines, setLines] = useState<UpsellLine[]>(() => cloneLines(upsell.lines));
  const [deletedLineIds, setDeletedLineIds] = useState<string[]>([]);
  const [newLineIds, setNewLineIds] = useState<Set<string>>(() => new Set());

  const resetFromUpsell = useCallback((source: UpsellWithLines) => {
    const baseline = buildBaseline(source);
    baselineRef.current = baseline;
    setBaselineVersion((v) => v + 1);
    setDescription(baseline.description);
    setOverheadInput(baseline.overhead_pct != null ? String(baseline.overhead_pct) : "");
    setDiscountPctInput(baseline.discount_pct ? String(baseline.discount_pct) : "");
    setDiscountAmountInput(baseline.discount_amount ? String(baseline.discount_amount) : "");
    setBillingMode(baseline.billing_mode);
    setLines(cloneLines(baseline.lines));
    setDeletedLineIds([]);
    setNewLineIds(new Set());
  }, []);

  // Init / re-init når dialogen åpnes eller mersalg byttes.
  const prevOpenRef = useRef(false);
  const upsellIdRef = useRef(upsell.id);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    const idChanged = upsell.id !== upsellIdRef.current;
    upsellIdRef.current = upsell.id;
    if (justOpened || (open && idChanged)) {
      resetFromUpsell(upsell);
    }
  }, [open, upsell, resetFromUpsell]);

  const isDirty = useMemo(() => {
    void baselineVersion;
    const b = baselineRef.current;
    if (description.trim() !== b.description) return true;
    if (parseOverhead(overheadInput) !== b.overhead_pct) return true;
    if (parseDiscount(discountPctInput) !== b.discount_pct) return true;
    if (parseDiscount(discountAmountInput) !== b.discount_amount) return true;
    if (billingMode !== b.billing_mode) return true;
    if (deletedLineIds.length > 0) return true;
    if (newLineIds.size > 0) return true;
    if (!linesEqual(lines, b.lines)) return true;
    return false;
  }, [
    baselineVersion,
    description,
    overheadInput,
    discountPctInput,
    discountAmountInput,
    billingMode,
    lines,
    deletedLineIds,
    newLineIds,
  ]);

  function setBillingModeLocal(mode: UpsellBillingMode) {
    setBillingMode(mode);
  }

  function updateLineLocal(lineId: string, patch: Partial<UpsellLine>) {
    setLines((prev) =>
      prev.map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
    );
  }

  function addLineFromTemplate(phase: OfferPhase, template: OfferLineTemplate) {
    const id = crypto.randomUUID();
    const line: UpsellLine = {
      id,
      phase,
      description: template.name,
      cost_type: template.cost_type,
      unit: template.default_unit,
      quantity: 0,
      quantity_hours: 0,
      amount: 0,
      estimated_cost: 0,
      upsell_id: upsell.id,
      template_id: template.id,
      // VIB-2097: katalogsatsen (kr/t) er tidsbasisen for en ny t/d-linje.
      rate_basis_hourly: templateHourlyRate(template) || null,
    };
    setLines((prev) => [...prev, line]);
    setNewLineIds((prev) => new Set(prev).add(id));
  }

  function addFreeTextLine(phase: OfferPhase) {
    const id = crypto.randomUUID();
    const line: UpsellLine = {
      id,
      phase,
      description: "Ny linje",
      cost_type: "external",
      unit: "stk",
      quantity: 0,
      quantity_hours: 0,
      amount: 0,
      estimated_cost: 0,
      upsell_id: upsell.id,
      template_id: null,
      rate_basis_hourly: null,
    };
    setLines((prev) => [...prev, line]);
    setNewLineIds((prev) => new Set(prev).add(id));
  }

  function deleteLineLocal(lineId: string) {
    const wasNew = newLineIds.has(lineId);
    const wasBaseline = baselineRef.current.lines.some((l) => l.id === lineId);
    setLines((prev) => prev.filter((l) => l.id !== lineId));
    if (wasNew) {
      setNewLineIds((prev) => {
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
      return;
    }
    if (wasBaseline) {
      setDeletedLineIds((prev) => (prev.includes(lineId) ? prev : [...prev, lineId]));
    }
  }

  function buildContainerPatch(): UpsellDraftContainerPatch | null {
    const b = baselineRef.current;
    const patch: UpsellDraftContainerPatch = {};
    const d = description.trim();
    if (d && d !== b.description) patch.description = d;
    const oh = parseOverhead(overheadInput);
    if (oh !== b.overhead_pct) patch.overhead_pct = oh;
    const dp = parseDiscount(discountPctInput);
    if (dp !== b.discount_pct) patch.discount_pct = dp;
    const da = parseDiscount(discountAmountInput);
    if (da !== b.discount_amount) patch.discount_amount = da;
    if (billingMode !== b.billing_mode) patch.billing_mode = billingMode;
    return Object.keys(patch).length > 0 ? patch : null;
  }

  function buildLineUpdates(): Array<{ budget_line_id: string; patch: UpsellLinePersistPatch }> {
    const baselineById = new Map(baselineRef.current.lines.map((l) => [l.id, l]));
    const updates: Array<{ budget_line_id: string; patch: UpsellLinePersistPatch }> = [];
    for (const line of lines) {
      if (newLineIds.has(line.id)) continue;
      const orig = baselineById.get(line.id);
      if (!orig) continue;
      if (lineFingerprint(line) === lineFingerprint(orig)) continue;
      updates.push({
        budget_line_id: line.id,
        patch: {
          description: line.description ?? undefined,
          phase: line.phase ?? undefined,
          cost_type: line.cost_type ?? undefined,
          unit: line.unit ?? undefined,
          quantity: line.quantity ?? 0,
          quantity_hours: line.quantity_hours ?? 0,
          amount: line.amount ?? 0,
          estimated_cost: line.estimated_cost ?? 0,
          rate_basis_hourly: line.rate_basis_hourly ?? null,
        },
      });
    }
    return updates;
  }

  async function save(): Promise<boolean> {
    if (!isDirty) return true;
    const inserts = lines.filter((l) => newLineIds.has(l.id));
    try {
      await saveMut.mutateAsync({
        upsell_id: upsell.id,
        project_id: projectId,
        containerPatch: buildContainerPatch(),
        inserts,
        updates: buildLineUpdates(),
        deleteIds: deletedLineIds,
      });
      // Marker draft som ren mot det vi nettopp lagret (før refetch).
      baselineRef.current = {
        description: description.trim() || baselineRef.current.description,
        overhead_pct: parseOverhead(overheadInput),
        discount_pct: parseDiscount(discountPctInput),
        discount_amount: parseDiscount(discountAmountInput),
        billing_mode: billingMode,
        lines: cloneLines(lines),
      };
      setDeletedLineIds([]);
      setNewLineIds(new Set());
      setBaselineVersion((v) => v + 1);
      return true;
    } catch {
      return false;
    }
  }

  function discard() {
    resetFromUpsell({
      ...upsell,
      description: baselineRef.current.description,
      overhead_pct: baselineRef.current.overhead_pct,
      discount_pct: baselineRef.current.discount_pct,
      discount_amount: baselineRef.current.discount_amount,
      billing_mode: baselineRef.current.billing_mode,
      lines: baselineRef.current.lines,
    } as UpsellWithLines);
  }

  return {
    description,
    setDescription,
    overheadInput,
    setOverheadInput,
    discountPctInput,
    setDiscountPctInput,
    discountAmountInput,
    setDiscountAmountInput,
    billingMode,
    setBillingMode: setBillingModeLocal,
    lines,
    updateLineLocal,
    addLineFromTemplate,
    addFreeTextLine,
    deleteLineLocal,
    isDirty,
    save,
    discard,
    isSaving: saveMut.isPending,
  };
}
